import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { AsyncStateN } from '@blade/std-lib'

import { TableFilter, type DataTableModel } from '../atoms'

import { DataTableLoading } from './data-table-loading'
import { DataTableMessage } from './data-table-message'

import type { Column } from './data-table-types'

export type DataTableEmptyPublicProps = {
  emptyTitle?: string
  renderError?: ((e: unknown) => React.ReactNode) | undefined
  renderEmptyMessage?: () => React.ReactNode
}

type DataTableEmptyProps = {
  columns: ReadonlyArray<Column>
  model: DataTableModel
} & DataTableEmptyPublicProps

export const DataTableEmpty = ({
  columns,
  model,
  renderError,
  renderEmptyMessage,
  emptyTitle,
}: DataTableEmptyProps) => {
  const { t } = useTranslation()
  const [itemsState] = useAtom(model)
  const resetFilters = useAction(model.clearColumnsFilters)
  const retry = useAction(model.load)
  const [filters] = useAtom(model.filters)

  const rowCount = AsyncStateN.isSuccess(itemsState)
    ? itemsState.value.data.length
    : 0

  const isSomeFilterApplied = useMemo(() => {
    return TableFilter.hasAnyFilter(filters)
  }, [filters])

  if (AsyncStateN.isLoading(itemsState)) {
    return <DataTableLoading />
  }

  if (AsyncStateN.isError(itemsState)) {
    if (renderError) {
      return (
        renderError(itemsState.error) ?? (
          <DataTableMessage
            title={t('common.error')}
            description={t('common.something-went-wrong')}
            onRetry={retry}
          />
        )
      )
    }

    return (
      <DataTableMessage
        title={t('common.error')}
        description={t('common.something-went-wrong')}
        onRetry={retry}
      />
    )
  }
  if (rowCount === 0) {
    const handleRefresh = () => {
      if (isSomeFilterApplied) {
        resetFilters(columns.map((x) => x.id))
      } else {
        retry()
      }
    }

    return (
      renderEmptyMessage?.() ?? (
        <DataTableMessage
          title={emptyTitle ?? t('common.no-data-found')}
          description={
            isSomeFilterApplied
              ? t('common.no-data-try-change-filters')
              : undefined
          }
          retryText={
            isSomeFilterApplied ? t('common.filter_reset') : t('common.refresh')
          }
          onRetry={handleRefresh}
        />
      )
    )
  }
}
